import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { integrationService } from '@/application/services';
import type { IntegrationStatus } from '@/infrastructure/repositories';

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

export const IntegrationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState<Record<string, IntegrationStatus>>({});
  
  useEffect(() => {
    loadStatuses();
  }, []);

  const loadStatuses = async () => {
    setLoading(true);
    try {
      // Временно отключен запрос к API, пока endpoint не будет готов
      // const avitoStatus = await integrationService.getStatus('avito');
      setStatuses({
        avito: {
          id: 'avito',
          connected: false,
          comingSoon: false,
        },
      });
    } catch (error) {
      console.error('Failed to load statuses:', error);
      setStatuses({
        avito: {
          id: 'avito',
          connected: false,
          comingSoon: false,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = (integrationId: string) => {
    if (integrationId === 'avito') {
      // TODO: Реализовать подключение интеграции
      // Пока просто переходим на страницу интеграции
      navigate(`/integration/${integrationId}`);
    }
  };
  
  const integrations: Integration[] = [
    {
      id: 'avito',
      name: 'Авито',
      description: 'Интеграция с маркетплейсом Авито',
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10.595" cy="5.225" r="3.325" fill="#965EEB"></circle>
          <circle cx="22.245" cy="7.235" r="7.235" fill="#00AAFF"></circle>
          <circle cx="8.9" cy="18.6" r="8.9" fill="#04E061"></circle>
          <circle cx="24.325" cy="21.005" r="5.375" fill="#FF4053"></circle>
        </svg>
      ),
      color: 'bg-gray-100',
    },
    {
      id: 'yandex-market',
      name: 'Яндекс Маркет',
      description: 'Интеграция с маркетплейсом Яндекс Маркет',
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 1000 833.2" xmlns="http://www.w3.org/2000/svg">
          <path fill="#FF5226" d="M529.3,833.2c-229.8,0-416.6-186.1-416.6-415.9C112.7,187.5,299.5,0,529.3,0c229.8,0,416.6,187.5,416.6,417.4 C945.9,647.2,759.1,833.2,529.3,833.2z"/>
          <path fill="#FFDD00" d="M464.8,56.4L0,781.6h213.1l268.5-418l-6.6-3l-74.4,299.1l157.6,27.8l162-203.6l-8.8-4.4l-54.7,231.2l343.4-65.4 L930,530l-123.8,28.3l14.6,16.8l57.6-241.5l-134.3-89.8L568.4,464.9l8.7,4.4l72.2-289.7C649.3,179.6,464.8,56.4,464.8,56.4z"/>
          <path fill="#FFDD00" d="M464.8,56.4L0,781.6h213.1l268.5-418l-6.6-3l-74.4,299.1l157.6,27.8l162-203.6l-8.8-4.4l-54.7,231.2l138-26.3 l83.8-350.9l-134.3-89.8L568.4,464.9l8.7,4.4l72.2-289.7C649.3,179.6,464.8,56.4,464.8,56.4z"/>
        </svg>
      ),
      color: 'bg-white dark:bg-gray-700',
    },
    {
      id: 'ozon',
      name: 'OZON',
      description: 'Интеграция с маркетплейсом OZON',
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 1001 1001" xmlns="http://www.w3.org/2000/svg">
          <rect fill="#005BFF" width="1001" height="1001" rx="200"/>
          <path fill="#FFFFFF" d="M419.9,576.5l-88.2,23.6l72.7-179.3c1.6-4-0.4-8.5-4.4-10.1c-1.5-0.6-3.2-0.7-4.9-0.3l-163,43.7c-14.4,3.9-22.9,18.6-19.1,33c3.9,14.4,18.6,22.9,33,19.1l73.3-19.6l-72.1,179.5c-1.7,3.9,0.2,8.5,4.1,10.1c1.6,0.7,3.4,0.8,5.1,0.3l178.4-47.8c14.2-4.6,21.9-19.8,17.3-34C447.9,581.2,434.5,573.4,419.9,576.5z M946.4,262.7c-14.4,3.9-22.9,18.6-19.1,33l23.4,87.4l-170.8-74c-3.9-1.8-8.5,0-10.2,3.9c-0.7,1.6-0.9,3.5-0.4,5.2l48.1,179.6c3.9,14.4,18.6,22.9,33,19.1c14.4-3.9,22.9-18.6,19.1-33l-23.4-87.4l170.8,74.1c4,1.7,8.5-0.2,10.2-4.1c0.7-1.6,0.8-3.4,0.4-5.1l-48.1-179.6C975.6,267.3,960.8,258.8,946.4,262.7z M618.3,528.6c-59.6,16-112.6-3.5-120.1-31.6c-7.5-28.1,28.6-71.4,88.2-87.4c59.6-16,112.6,3.5,120.1,31.6C714.1,469.3,678,512.6,618.3,528.6z M572.5,357.6c-86.3,23.1-142.8,91.8-126.3,153.4c16.5,61.6,99.8,92.8,186.1,69.7c86.3-23.1,142.8-91.8,126.3-153.4C742.1,365.6,658.8,334.4,572.5,357.6z M105.5,666.1c-32.9,8.9-66.7-10.6-75.6-43.5c-8.9-32.9,10.6-66.7,43.5-75.6c32.9-8.9,66.7,10.6,75.6,43.5C157.9,623.4,138.4,657.2,105.5,666.1z M59.7,495c-61.6,16.5-98.3,79.8-81.8,141.5c16.5,61.7,79.8,98.3,141.5,81.8c61.6-16.5,98.3-79.8,81.8-141.5C184.6,515,121.3,478.5,59.7,495z"/>
        </svg>
      ),
      color: 'bg-white dark:bg-gray-700',
    },
    {
      id: 'legitfam',
      name: 'LegitFam',
      description: 'Интеграция платформой с legitfam.pro',
      icon: (
        <svg className="w-12 h-12" viewBox="0 0 163 167" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M63.1444 0C59.9002 0 57.2703 2.5636 57.2703 5.72591V27.6741C57.2703 30.8361 59.9002 33.3997 63.1444 33.3997H122.862C126.106 33.3997 128.736 35.9632 128.736 39.1251V61.0734C128.736 64.2357 126.106 66.7992 122.862 66.7992H66.0813C62.8371 66.7992 60.2072 69.3623 60.2072 72.5246V94.4731C60.2072 97.6353 62.8371 100.198 66.0813 100.198H122.862C126.106 100.198 128.736 102.762 128.736 105.924V161.275C128.736 164.436 131.366 167 134.61 167H157.126C160.37 167 163 164.436 163 161.275V5.72591C163 2.5636 160.37 0 157.126 0H63.1444Z" fill="#24BA7D"/>
          <path d="M33.775 127.874V5.72562C33.775 2.5636 31.1826 0 27.9848 0H5.7899C2.59237 0 0 2.5636 0 5.72562V161.275C0 164.436 2.59237 167 5.7899 167H95.5345C98.7323 167 101.325 164.436 101.325 161.275V139.325C101.325 136.163 98.7323 133.6 95.5345 133.6H39.5648C36.3669 133.6 33.775 131.037 33.775 127.874Z" fill="white"/>
        </svg>
      ),
      color: 'bg-gray-800 dark:bg-gray-700',
    },
    {
      id: 'telegram-bot',
      name: 'Telegram Bot',
      description: 'Интеграция с Telegram ботом',
      icon: (
        <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.17 1.903-.896 6.527-1.266 8.646-.16.863-.475 1.151-.78 1.179-.66.054-1.162-.437-1.802-.857-1-.66-1.565-1.07-2.537-1.714-1.122-.733-.394-1.136.244-1.796.168-.172 3.062-2.81 3.12-3.048.007-.03.014-.149-.057-.21-.071-.061-.176-.038-.252-.023-.107.023-1.793 1.14-5.062 3.346-.479.324-.913.482-1.302.474-.428-.009-1.25-.241-1.861-.44-.751-.244-1.348-.373-1.297-.788.027-.218.324-.441.893-.669 3.498-1.524 5.83-2.529 6.998-3.014 3.331-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.14.118.095.151.223.167.313.016.09.036.297.02.458z"/>
        </svg>
      ),
      color: 'bg-sky-500',
    },
  ];

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-7xl mx-auto">
        <div className="pb-4 border-b border-gray-200 dark:border-gray-700 mb-6">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Интеграции</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Подключите внешние сервисы и маркетплейсы для автоматизации работы</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {integrations.map((integration) => (
            <div
              key={integration.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow p-6 relative overflow-hidden"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`${integration.color} ${integration.color === 'bg-gray-100' ? 'dark:bg-gray-700' : ''} ${integration.color === 'bg-gray-100' ? '' : 'text-white'} rounded-lg p-3 flex-shrink-0`}>
                  {integration.icon}
                </div>
                <div className="absolute top-4 right-4">
                  {integration.id === 'avito' ? (
                    // Для Авито показываем статус из API
                    !loading && statuses[integration.id]?.connected ? (
                      <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold px-3 py-1 rounded-full">
                        Активно
                      </span>
                    ) : null
                  ) : (
                    // Для всех остальных всегда показываем "СКОРО"
                    <span className="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-semibold px-3 py-1 rounded-full">
                      СКОРО
                    </span>
                  )}
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{integration.name}</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">{integration.description}</p>
              
              {integration.id === 'avito' ? (
                // Для Авито кнопка всегда активна
                !loading && statuses[integration.id]?.connected ? (
                  <button
                    onClick={() => navigate(`/integration/${integration.id}`)}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Открыть
                  </button>
                ) : (
                  <button
                    onClick={() => handleConnect(integration.id)}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Подключить
                  </button>
                )
              ) : (
                // Для всех остальных кнопка неактивна
                <button
                  disabled
                  className="w-full bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 px-4 py-2 rounded-lg text-sm font-medium cursor-not-allowed"
                >
                  Подключить
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

